import * as React from 'react'

import { createContext } from '~logic/lib/react'

import { useLoadAddonDetail } from '../hooks/useLoadAddonDetail'
import { useAddonDetailCtx } from './AddonDetailProvider'

type TAddonInList = Defined<ReturnType<typeof useAddonDetailCtx>['addon']>
type TAddonDetail = Defined<ReturnType<typeof useLoadAddonDetail>['addonDetail']>
type TAddon = TAddonInList & Partial<TAddonDetail>

type TAddonContext = {
  addon: TAddon
  isFetching: boolean
}

const [Provider, useAddonDetailStateCtx] = createContext<TAddonContext>(`AddonDetailState`)

type TProps = RequiredChildren & {
  branchId: FirstParam<typeof useLoadAddonDetail>['branchId']
}

/**
 * Loads additional data needed to render MAIN addon detail.
 * Once it is loaded, it gets combined with the properties addon-in-list has
 * (until then, only those are provided).
 */
export const AddonDetailStateProvider: React.FC<TProps> = ({ branchId, children }) => {
  const { addon } = useAddonDetailCtx()
  const detailData = useLoadAddonDetail({ branchId, addonId: addon?.id ?? null })

  // combine addon detail info (once loaded) with the addon-in-list info
  const addonDetail = React.useMemo(() => {
    return addon && detailData.addonDetail && !detailData.loading
      ? { ...addon, ...detailData.addonDetail }
      : addon
  }, [addon, detailData.addonDetail, detailData.loading])

  if (!addonDetail) {
    return null
  }

  return (
    <Provider value={{ addon: addonDetail, isFetching: detailData.loading }}>{children}</Provider>
  )
}

export { useAddonDetailStateCtx }
